import {Plug, PlugKind, PlugsView} from "../bid/bidding_model";
import {API_URL, putWithToken} from "../../api/apiBase";
import {moneyFormatter} from "../../lib/formatters";
import {Cell, RowMeta, SovConfig} from "../project_conf/bidConfig_model";

// plugs are keyed by SOV cell id. base bid plugs (cell_id null) go under BASE
export const BASE = "base";
export const cellKey = (cell_id?: number | null) => cell_id == null ? BASE : String(cell_id);

export function indexPlugs(plugs: Plug[] = []): Record<string, Plug[]> {
    const out: Record<string, Plug[]> = {};
    for (const p of plugs) {
        const k = cellKey(p.cell_id);
        if (!out[k]) out[k] = [];
        out[k].push(p);
    }
    return out;
}

export const PLUG_KINDS: { value: PlugKind, label: string }[] = [
    {value: "add", label: "ADD"},
    {value: "alt", label: "ALT"},
    {value: "osv", label: "OSV"},
    {value: "info", label: "Info"},
    {value: "included", label: "Included"},
    {value: "excluded", label: "Excluded"},
];

export const kindLabel = (kind: PlugKind) => PLUG_KINDS.find((k) => k.value === kind)?.label ?? kind;

// included / excluded are a yes / no on the line - no amount. plugs stored before kinds existed are adds
export const hasAmount = (kind?: PlugKind) => kind !== "included" && kind !== "excluded";

// included / excluded on a bidder's cell - a larger tag. bid page and compare
export const MARK_TAG = "rounded px-2 py-0.5 text-xs font-medium";

// tag = kind label / select / compare badge. line = the plug's line under the SOV row.
// alt / info aren't counted - muted, but the text has to stay readable (eg a bidder's breakout as info)
const KIND_STYLE: Record<PlugKind, { tag: string, line: string }> = {
    add: {tag: "bg-green-100 text-green-800", line: "bg-green-50"},
    alt: {tag: "bg-amber-100 text-amber-800", line: "bg-amber-50 text-gray-600"},
    osv: {tag: "bg-[#ecdccb] text-[#6f4a2a]", line: "bg-[#f7efe7]"},
    info: {tag: "bg-gray-200 text-gray-600", line: "bg-gray-50 text-gray-600"},
    included: {tag: "bg-emerald-100 text-emerald-800", line: "bg-emerald-50"},
    excluded: {tag: "bg-red-100 text-red-800", line: "bg-red-50"},
};

// plugs stored before kinds existed are adds
export const kindStyle = (kind?: PlugKind) => KIND_STYLE[kind ?? "add"] ?? KIND_STYLE.add;

// a plug carried from the bidder's previous bid (stale) - until the developer checks it in the plug popover.
// an outline, so it sits over the kind's colours without moving anything
export const STALE_PLUG = "outline-dotted outline-2 outline-red-500";
export const STALE_TITLE = "Carried from the previous bid - check it in the plug popup";

// developer only lines - violet, so they never clash with the plug kinds.
// one place for the template editor, the bid page and compare
export const DEV_LINE = {
    row: "bg-violet-50",
    cell: "bg-violet-50 border-violet-200",
    // an input on a developer only line - sits on the violet row, a border shows on hover / focus
    input: "bg-transparent border-transparent hover:border-violet-300 focus:border-violet-400 " +
        "focus:outline-none placeholder:text-violet-300",
    tag: "bg-violet-100 text-violet-800",
};

// "$15,000" / "15000" / "" -> number or null
export function parseCellValue(v: any): number | null {
    const n = parseFloat(String(v ?? "").replace(/[$,\s]/g, ""));
    return isNaN(n) ? null : n;
}

// $ inputs on developer only lines - same as backend plug_service.developer_line_total
export function devLineSum(data: Cell[][], rowMeta: RowMeta[] = []): number {
    let total = 0;
    data.forEach((row, i) => {
        if (!rowMeta[i] || !rowMeta[i].developer_only) return;
        row.forEach((cell) => {
            if (cell.party && (!cell.type || cell.type === "$")) {
                total += parseCellValue(cell.value) ?? 0;
            }
        });
    });
    return Math.round(total);
}

// same as backend plug_model.COUNTED_KINDS. plugs stored before kinds existed are adds
export const isCounted = (p: Plug) => !p.kind || p.kind === "add" || p.kind === "osv";

// what goes into the leveled amount
export const plugSum = (plugs: Plug[] = []) => plugs.filter(isCounted).reduce((s, p) => s + p.amount, 0);

// "+$15,000" / "-$5,000"
export const signedMoney = (n: number) => (n < 0 ? "-" : "+") + moneyFormatter.format(Math.abs(n));

// "$15,000", "-5000" -> whole dollars. null if not a number
export function parseAmount(s: string): number | null {
    const n = parseFloat(s.replace(/[$,\s]/g, ""));
    return isNaN(n) ? null : Math.round(n);
}

/**
 * The plug popover's calls. Like apiBase's helpers, but a failed call's message is the server's reason,
 * so the popover's toast can say what went wrong. main.py's handlers send HTTPExceptions as {message: "..."}
 * (and anything unhandled as 400 {message: "Generic Error "}); validation errors are {detail: [...]}.
 * */
export async function levelingRequest(method: string, url: string, token: string, body?: any): Promise<any> {
    const response = await fetch(`${API_URL}${url}`, {
        method,
        body: body === undefined ? undefined : JSON.stringify(body),
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        throw new Error(await failureReason(response));
    }
    return response.json();
}

async function failureReason(response: Response): Promise<string> {
    try {
        const data = await response.json();
        if (data && typeof data.message === "string") {
            return data.message.trim();
        }
        const detail = data ? data.detail : undefined;
        if (typeof detail === "string") {
            return detail;
        }
        if (Array.isArray(detail)) {
            return detail.map((d: any) =>
                (Array.isArray(d.loc) ? d.loc.slice(1).join(".") + ": " : "") + d.msg).join("; ");
        }
    } catch (e) {
        // not json - eg a 500 page
    }
    return `${response.status} ${response.statusText}`;
}

const plugsUrl = (project_id: string, bid_id: string) => `/projects/${project_id}/bidding/${bid_id}/plugs`;

// cell_id - the SOV input cell, null for the base bid
export function addPlug(project_id: string, bid_id: string, cell_id: number | null,
                        data: { amount: number, note: string, kind: PlugKind }, token: string): Promise<PlugsView> {
    return levelingRequest("POST", plugsUrl(project_id, bid_id), token, {cell_id, ...data});
}

export function updatePlug(project_id: string, bid_id: string, plug_id: string,
                           data: { amount?: number, note?: string, kind?: PlugKind, stale?: boolean },
                           token: string): Promise<PlugsView> {
    return levelingRequest("PUT", `${plugsUrl(project_id, bid_id)}/${plug_id}`, token, data);
}

export function deletePlug(project_id: string, bid_id: string, plug_id: string, token: string): Promise<PlugsView> {
    return levelingRequest("DELETE", `${plugsUrl(project_id, bid_id)}/${plug_id}`, token);
}

// adds a developer only line to the SOV under the plug's line.
// the plug's amount becomes the line's value on this bid (backend plug_service.moves_into_line)
export function plugToLine(project_id: string, bid_id: string, plug_id: string, token: string): Promise<PlugsView> {
    return levelingRequest("POST", `${plugsUrl(project_id, bid_id)}/${plug_id}/line`, token, {});
}

// developer only lines - edits to the SOV (server bid_sov_routes). each returns the whole SOV

// rename a developer only line - cell_id is its first cell
export function setDevLineLabel(project_id: string, config_id: string, cell_id: number,
                                label: string, token: string): Promise<SovConfig> {
    return levelingRequest("PUT", `/projects/${project_id}/sov/${config_id}/cells/${cell_id}`, token, {text: label});
}

// change the type ($ / num / txt) of an input on a developer only line
export function setDevLineType(project_id: string, config_id: string, cell_id: number,
                               type: string, token: string): Promise<SovConfig> {
    return levelingRequest("PUT", `/projects/${project_id}/sov/${config_id}/cells/${cell_id}`, token, {type});
}

// delete a developer only line - by its index in the SOV. its values stay on the bids, never shown
export function deleteDevLine(project_id: string, config_id: string, index: number,
                              token: string): Promise<SovConfig> {
    return levelingRequest("DELETE", `/projects/${project_id}/sov/${config_id}/lines/${index}`, token);
}

// a blank developer only line under the line holding cell_id - a text input in its column
export function addDevLine(project_id: string, config_id: string, cell_id: number,
                           token: string): Promise<SovConfig> {
    return levelingRequest("POST", `/projects/${project_id}/sov/${config_id}/lines`, token,
        {after: cell_id, developer_only: true, input_type: "txt"});
}

// the developer's own value in a developer only cell - a number, or text on a text line. null clears it
export function setDeveloperCell(project_id: string, bid_id: string, cell_id: number,
                                 value: number | string | null, token: string): Promise<PlugsView> {
    return putWithToken(`/projects/${project_id}/bidding/${bid_id}/cells`, JSON.stringify({cell_id, value}), token);
}
